import {
  AlertTriangle,
  CircleAlert,
  TrendingDown,
  Lightbulb,
  Gift,
  Scale,
  Dumbbell,
  Flame,
  CheckCircle2,
  Star,
  BookOpen,
  BarChart3,
  RefreshCcw,
  Shield,
  Construction,
  Target,
  type LucideIcon,
} from "lucide-react";
import type { ComputedResults, DiagnosticInsight, SimulationInput } from "./types";

export function buildInsights(
  input: SimulationInput,
  r: ComputedResults
): DiagnosticInsight[] {
  const insights: DiagnosticInsight[] = [];

  // ===== ALERTES (priorité haute) =====
  if (r.cashFlowMensuel < 0) {
    insights.push({
      id: "cf-neg",
      type: "alert",
      icon: AlertTriangle,
      title: `Cash-flow négatif — vous devez sortir ${Math.round(
        Math.abs(r.cashFlowMensuel)
      )}€/mois de votre poche`,
      priority: 100,
    });
  }
  if (r.ratioCharges > 0.5) {
    insights.push({
      id: "charges-high",
      type: "alert",
      icon: CircleAlert,
      title: `Charges élevées (${Math.round(r.ratioCharges * 100)}% des loyers) — à investiguer`,
      priority: 90,
    });
  }
  if (input.tauxVacance > 15 && input.typeLocation === "longue") {
    insights.push({
      id: "vacance-high",
      type: "alert",
      icon: AlertTriangle,
      title: "Vacance élevée pour de la longue durée, revoyez votre loyer",
      priority: 80,
    });
  }
  if (r.rendementBrut < 4 && r.rendementBrut > 0) {
    insights.push({
      id: "rendement-low",
      type: "alert",
      icon: TrendingDown,
      title: `Rendement brut faible (${r.rendementBrut.toFixed(
        1
      )}%), l'emplacement doit compenser`,
      priority: 70,
    });
  }

  // ===== OPPORTUNITÉS =====
  if (input.chargesMode === "simple" && input.chargesPct > 30) {
    insights.push({
      id: "switch-detailed",
      type: "opportunity",
      icon: Lightbulb,
      title:
        "Passez en charges détaillées : vous pouvez peut-être déduire plus que ce que vous estimez",
      priority: 75,
    });
  }
  if (input.chargesMode === "detaille" && input.chargesDetail.comptabilite === 0) {
    insights.push({
      id: "compta",
      type: "opportunity",
      icon: Lightbulb,
      title: "+400€/an d'économies fiscales avec un comptable (déductible)",
      priority: 65,
    });
  }
  if (r.amortissementAnnuel > r.revenusBrutsAnnuels) {
    insights.push({
      id: "amort-shield",
      type: "opportunity",
      icon: Gift,
      title:
        "Amortissements > revenus : vous ne paierez pas d'impôts sur ces loyers pendant des années",
      priority: 85,
    });
  }
  if (
    input.typeLocation === "courte" &&
    input.tauxVacance > 25
  ) {
    insights.push({
      id: "bail-mobilite",
      type: "opportunity",
      icon: Lightbulb,
      title: "Testez le bail mobilité : revenus +15% en moyenne sur ce profil",
      priority: 60,
    });
  }
  if (r.impotsReel < r.impotsMicroBIC - 200) {
    insights.push({
      id: "regime-reel",
      type: "opportunity",
      icon: Scale,
      title: `Régime réel : ${Math.round(
        r.impotsMicroBIC - r.impotsReel
      )}€/an d'impôts économisés vs micro-BIC`,
      priority: 70,
    });
  }

  // ===== AFFILIATIONS CONTEXTUELLES =====
  if (input.hasCredit && input.tauxAnnuel > 3.9) {
    insights.push({
      id: "taux-eleve",
      type: "alert",
      icon: AlertTriangle,
      title: `Votre taux (${input.tauxAnnuel.toFixed(2)}%) est supérieur à la moyenne du marché (3.85%)`,
      priority: 78,
      affiliate: "credit",
    });
  }
  if (input.hasCredit && input.assuranceEmprunteurPct > 0.3) {
    const economie = Math.round(
      (input.assuranceEmprunteurPct - 0.1) / 100 * input.montantEmprunte * input.dureeAnnees
    );
    insights.push({
      id: "assurance-eleve",
      type: "opportunity",
      icon: Lightbulb,
      title: `Votre assurance emprunteur pourrait être optimisée — économie potentielle ~${economie.toLocaleString("fr-FR")}€ sur la durée du prêt`,
      priority: 68,
      affiliate: "assurance",
    });
  }
  if (!input.hasCredit && r.rendementNet > 5) {
    insights.push({
      id: "no-credit-leverage",
      type: "opportunity",
      icon: Dumbbell,
      title: `Avec un bon rendement de ${r.rendementNet.toFixed(1)}%, l'effet de levier du crédit pourrait doubler votre ROI`,
      priority: 62,
      affiliate: "credit",
    });
  }

  // ===== POSITIVES =====
  if (r.cashFlowMensuel > 500) {
    insights.push({
      id: "cf-top",
      type: "positive",
      icon: Flame,
      title: "Cash-flow exceptionnel — vous êtes dans le top 20%",
      priority: 50,
    });
  }
  if (r.ratioCharges > 0 && r.ratioCharges < 0.25) {
    insights.push({
      id: "charges-low",
      type: "positive",
      icon: CheckCircle2,
      title: "Charges très bien maîtrisées",
      priority: 40,
    });
  }
  if (r.roiApport !== null && r.roiApport > 10) {
    insights.push({
      id: "leverage",
      type: "positive",
      icon: Dumbbell,
      title: "Excellent effet de levier bancaire",
      priority: 45,
    });
  }
  if (r.rendementNet > 6) {
    insights.push({
      id: "rendement-top",
      type: "positive",
      icon: Star,
      title: "Rendement supérieur à 95% des LMNP en France",
      priority: 55,
    });
  }

  // Tri par priorité (alertes d'abord), max 6 (un peu plus pour laisser de la place aux affiliés)
  return insights.sort((a, b) => b.priority - a.priority).slice(0, 6);
}

export interface Optimization {
  id: string;
  title: string;
  gainEstime: string;
  description: string;
  icon: LucideIcon;
}

export function buildOptimizations(
  input: SimulationInput,
  r: ComputedResults
): Optimization[] {
  const opts: Optimization[] = [];

  if (input.chargesMode === "detaille" && input.chargesDetail.comptabilite === 0) {
    opts.push({
      id: "compta",
      icon: BookOpen,
      title: "Déléguez votre comptabilité",
      gainEstime: "+300 à 500 €/an",
      description:
        "Un expert-comptable LMNP optimise vos amortissements et déductions. Le coût (~500€) est lui-même déductible.",
    });
  }
  if (input.chargesMode === "simple" && input.chargesPct > 35) {
    const gain = Math.round(r.revenusBrutsAnnuels * 0.05);
    opts.push({
      id: "switch-detail",
      icon: BarChart3,
      title: "Passez au régime réel détaillé",
      gainEstime: `+${gain} €/an estimés`,
      description:
        "En détaillant vos charges réelles, vous déduisez généralement 5 à 10% de plus qu'en estimation.",
    });
  }
  if (input.typeLocation === "courte" && input.tauxVacance > 25) {
    const gain = Math.round(input.loyerMensuel * 12 * 0.15);
    opts.push({
      id: "bail-mobilite",
      icon: RefreshCcw,
      title: "Basculez vers le bail mobilité",
      gainEstime: `+${gain} €/an potentiels`,
      description:
        "Cible étudiants/cadres en mission : vacance plus faible que la courte durée pure.",
    });
  }
  if (input.chargesMode === "detaille" && input.chargesDetail.assurancePNO < 100) {
    opts.push({
      id: "gli",
      icon: Shield,
      title: "Assurance loyers impayés (GLI)",
      gainEstime: "Sécurité + déduction",
      description:
        "Environ 3% des loyers, totalement déductible. Couvre jusqu'à 24 mois d'impayés.",
    });
  }
  if (r.amortissementAnnuel < r.revenusBrutsAnnuels * 0.5) {
    opts.push({
      id: "amort",
      icon: Construction,
      title: "Maximisez vos amortissements",
      gainEstime: "Économie d'impôts",
      description:
        "Faites valoriser séparément agencements, gros électroménager : durées d'amortissement plus courtes = plus de déduction par an.",
    });
  }
  // Toujours au moins 3
  if (opts.length < 3) {
    opts.push({
      id: "audit",
      icon: Target,
      title: "Audit personnalisé par un expert",
      gainEstime: "ROI moyen 10x",
      description:
        "Un audit LMNP révèle en moyenne 800 à 2000 € d'économies fiscales annuelles passées inaperçues.",
    });
  }
  return opts.slice(0, 4);
}
